import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Customer } from "@/gql/graphql";
import { cn } from "@/lib/utils";
import { useCustomerStep } from "../../../hooks/use-customer-step";

interface MobileCustomerStepProps {
  customerStep: ReturnType<typeof useCustomerStep>;
  loading?: boolean;
}

export function MobileCustomerStep({ customerStep, loading = false }: MobileCustomerStepProps) {
  const { t } = useTranslation("appointments");
  const {
    customerInput,
    customerEmail,
    customerPhone,
    showSuggestions,
    selectedCustomer,
    inputRef,
    customersData,
    customersLoading,
    handleCustomerSelect,
    handleCustomerInputChange,
    setCustomerEmail,
    setCustomerPhone,
    setShowSuggestions,
    emailValidationState,
    readonly,
  } = customerStep;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-4">{t("booking.customerStep.customerInfo")}</h3>

        <div className="space-y-4">
          <div className="relative">
            <Label htmlFor="customer">{t("booking.customerStep.labels.customer")} *</Label>
            <Input
              id="customer"
              ref={inputRef}
              placeholder={
                readonly
                  ? t("booking.customerStep.placeholders.customerSelected")
                  : t("booking.customerStep.placeholders.searchOrEnter")
              }
              value={customerInput}
              onChange={(e) => handleCustomerInputChange(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              disabled={loading || readonly}
              className="mt-1"
              readOnly={readonly}
              data-test-id="appointment-create-customer-search"
            />

            {/* Customer Suggestions Dropdown */}
            {!readonly && showSuggestions && customerInput.trim() && (
              <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {customersLoading ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="ml-2 text-sm">
                      {t("booking.customerStep.messages.searching")}
                    </span>
                  </div>
                ) : customersData?.customers?.customers?.length ? (
                  customersData.customers.customers.map((customer) => (
                    <button
                      type="button"
                      key={customer.id}
                      className="w-full text-left p-3 hover:bg-accent cursor-pointer border-b last:border-b-0 active:bg-accent"
                      onClick={() => handleCustomerSelect(customer as Customer)}
                      data-test-id={`appointment-create-customer-option-${customer.id}`}
                    >
                      <div className="font-medium text-sm">
                        {customer.firstName} {customer.lastName}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {customer.email && customer.primaryPhone?.phone
                          ? `${customer.email} • ${customer.primaryPhone.phone}`
                          : customer.email ||
                            customer.primaryPhone?.phone ||
                            t("booking.customerStep.messages.noContactInfo")}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-3 text-xs text-muted-foreground text-center">
                    {t("booking.customerStep.messages.noCustomersFound")}
                  </div>
                )}
              </div>
            )}

            {/* Status indicator */}
            {customerInput.trim() && (
              <div className="text-xs mt-2">
                {selectedCustomer ? (
                  <div className="text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-2 rounded text-xs">
                    ✓{" "}
                    {t("booking.customerStep.messages.selectedCustomer", {
                      firstName: selectedCustomer.firstName,
                      lastName: selectedCustomer.lastName,
                    })}
                  </div>
                ) : (
                  <div className="text-foreground bg-muted p-2 rounded text-xs">
                    ✓ {t("booking.customerStep.messages.willCreateNew", { name: customerInput })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="email">{t("booking.customerStep.labels.email")}</Label>
              <div className="relative">
                <Input
                  id="email"
                  placeholder={t("booking.customerStep.placeholders.emailOptional")}
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  disabled={loading || !!selectedCustomer || readonly}
                  readOnly={readonly}
                  className={cn(
                    "mt-1",
                    // Add border styling based on validation state
                    !selectedCustomer &&
                      customerEmail.trim() &&
                      (emailValidationState.emailExists === true
                        ? "border-red-500 focus:border-red-500"
                        : emailValidationState.emailExists === false &&
                            emailValidationState.validatedEmail === customerEmail.trim()
                          ? "border-green-500 focus:border-green-500"
                          : "")
                  )}
                />

                {/* Email validation indicator */}
                {!selectedCustomer && customerEmail.trim() && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {emailValidationState.isValidating ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : emailValidationState.emailExists === true ? (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    ) : emailValidationState.emailExists === false &&
                      emailValidationState.validatedEmail === customerEmail.trim() ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : null}
                  </div>
                )}
              </div>

              {/* Email validation message */}
              {!selectedCustomer && customerEmail.trim() && (
                <div className="mt-1">
                  {emailValidationState.error ? (
                    <p className="text-xs text-red-600">{emailValidationState.error}</p>
                  ) : emailValidationState.emailExists === true ? (
                    <p className="text-xs text-red-600">
                      {t("booking.customerStep.messages.emailAlreadyExists", {
                        defaultValue: "A customer with this email already exists",
                      })}
                    </p>
                  ) : emailValidationState.emailExists === false &&
                    emailValidationState.validatedEmail === customerEmail.trim() ? (
                    <p className="text-xs text-green-600">
                      {t("booking.customerStep.messages.emailAvailable", {
                        defaultValue: "Email is available",
                      })}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="phone">{t("booking.customerStep.labels.phone")}</Label>
              <Input
                id="phone"
                placeholder={t("booking.customerStep.placeholders.phoneOptional")}
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                disabled={loading || !!selectedCustomer || readonly}
                readOnly={readonly}
                className="mt-1"
                data-test-id="appointment-create-customer-phone"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
